import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useState } from 'react';
import type { BgmSoundType, PageTransitionType } from '../../hooks/useReaderSettings';
import { CompassIcon } from '../Icons';
import { AudioSettingsSection } from './AudioSettingsSection';
import { VisualEffectsSection } from './VisualEffectsSection';

export interface CommonSettingsTabProps {
  pageTransition: PageTransitionType;
  setPageTransition: (t: PageTransitionType) => void;
  radialMenuEnabled: boolean;
  setRadialMenuEnabled: (v: boolean) => void;
  bookmarkGestureEnabled: boolean;
  setBookmarkGestureEnabled: (v: boolean) => void;
  shareCardEnabled: boolean;
  setShareCardEnabled: (v: boolean) => void;
  showRemainingTime: boolean;
  setShowRemainingTime: (v: boolean) => void;
  bgmEnabled: boolean;
  setBgmEnabled: (v: boolean) => void;
  bgmVolume: number;
  setBgmVolume: (v: number) => void;
  bgmSound: BgmSoundType;
  setBgmSound: (s: BgmSoundType) => void;
  soundVolume: number;
  setSoundVolume: (v: number) => void;
  hapticsEnabled: boolean;
  setHapticsEnabled: (v: boolean) => void;
  glowEnabled: boolean;
  setGlowEnabled: (v: boolean) => void;
  glowIntensity: number;
  setGlowIntensity: (v: number) => void;
  tateChuYokoEnabled: boolean;
  setTateChuYokoEnabled: (v: boolean) => void;
  focusModeEnabled: boolean;
  setFocusModeEnabled: (v: boolean) => void;
  focusLineCount: number;
  setFocusLineCount: (v: number) => void;
  isDark: boolean;
}

export const CommonSettingsTab: React.FC<CommonSettingsTabProps> = ({
  pageTransition,
  setPageTransition,
  radialMenuEnabled,
  setRadialMenuEnabled,
  bookmarkGestureEnabled,
  setBookmarkGestureEnabled,
  shareCardEnabled,
  setShareCardEnabled,
  showRemainingTime,
  setShowRemainingTime,
  bgmEnabled,
  setBgmEnabled,
  bgmVolume,
  setBgmVolume,
  bgmSound,
  setBgmSound,
  soundVolume,
  setSoundVolume,
  hapticsEnabled,
  setHapticsEnabled,
  glowEnabled,
  setGlowEnabled,
  glowIntensity,
  setGlowIntensity,
  tateChuYokoEnabled,
  setTateChuYokoEnabled,
  focusModeEnabled,
  setFocusModeEnabled,
  focusLineCount,
  setFocusLineCount,
  isDark,
}) => {
  const [showInteractionsAccordion, setShowInteractionsAccordion] = useState(false);
  const [showEffectsAccordion, setShowEffectsAccordion] = useState(false);

  const activeBtnBg = isDark
    ? 'bg-neutral-700 text-[#FAF8F5]'
    : 'bg-[#FFFFFF] text-[#111111] shadow-xs border border-[#EAEAEA]';
  const inactiveBtnBg = isDark
    ? 'bg-[#1C1C1E] text-[#8E8E93] hover:text-[#FAF8F5]'
    : 'bg-[#F3F3F2] text-[#8E8E93] hover:text-[#111111]';

  return (
    <>
      {/* === 読書補助設定グループ === */}
      <div className="space-y-4">
        <span
          className={
            'text-[10px] font-bold tracking-widest uppercase opacity-45 block border-b pb-1 border-current/10'
          }
        >
          読書補助 ＆ 操作
        </span>

        {/* 1. ページめくり効果 */}
        <div className="space-y-1.5">
          <span className={'text-[10px] font-bold tracking-widest uppercase opacity-50'}>
            ページめくり効果
          </span>
          <div className="grid grid-cols-3 gap-2">
            {(
              [
                { id: 'snap', label: 'スナップ' },
                { id: 'fade-slide', label: 'フェード' },
                { id: 'curl', label: '3Dカール' },
              ] as const
            ).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setPageTransition(t.id)}
                className={`min-h-11 rounded-md text-[11px] font-bold px-1 transition-all ${
                  pageTransition === t.id ? activeBtnBg : inactiveBtnBg
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* 2. 縦中横の自動適用 (縦書き時) */}
        <div className="flex items-center justify-between p-3 rounded-md border border-neutral-500/10">
          <div className="space-y-0.5">
            <div className="text-xs font-bold">縦中横 (たてちゅうよこ)</div>
            <div className="text-[10px] opacity-40">縦書き時に半角英数字を正立表示</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer select-none">
            <input
              type="checkbox"
              checked={tateChuYokoEnabled}
              onChange={(e) => setTateChuYokoEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-neutral-300 dark:bg-neutral-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-neutral-850 dark:peer-checked:bg-neutral-100" />
          </label>
        </div>

        {/* 3. しおりドラッグジェスチャー */}
        <div className="flex items-center justify-between p-3 rounded-md border border-neutral-500/10">
          <div className="space-y-0.5">
            <div className="text-xs font-bold">しおりジェスチャー</div>
            <div className="text-[10px] opacity-40">画面上端のしおりを引き下ろして挟む</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer select-none">
            <input
              type="checkbox"
              checked={bookmarkGestureEnabled}
              onChange={(e) => setBookmarkGestureEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-neutral-300 dark:bg-neutral-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-neutral-850 dark:peer-checked:bg-neutral-100" />
          </label>
        </div>

        {/* 4. ラジアルメニュー */}
        <div className="flex items-center justify-between p-3 rounded-md border border-neutral-500/10">
          <div className="space-y-0.5">
            <div className="text-xs font-bold">ラジアルメニュー</div>
            <div className="text-[10px] opacity-40">画面中央タップ時に円形メニューを展開</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer select-none">
            <input
              type="checkbox"
              checked={radialMenuEnabled}
              onChange={(e) => setRadialMenuEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-neutral-300 dark:bg-neutral-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-neutral-850 dark:peer-checked:bg-neutral-100" />
          </label>
        </div>

        {/* 5. フォーカス・リーディングモード ＆ アンビエント・グロウ */}
        <VisualEffectsSection
          focusModeEnabled={focusModeEnabled}
          setFocusModeEnabled={setFocusModeEnabled}
          focusLineCount={focusLineCount}
          setFocusLineCount={setFocusLineCount}
          glowEnabled={glowEnabled}
          setGlowEnabled={setGlowEnabled}
          glowIntensity={glowIntensity}
          setGlowIntensity={setGlowIntensity}
        />

        {/* 6. SNSシェアカード機能 */}
        <div className="flex items-center justify-between p-3 rounded-md border border-neutral-500/10">
          <div className="space-y-0.5">
            <div className="text-xs font-bold">SNSシェアカード生成</div>
            <div className="text-[10px] opacity-40">選択メニューにグラフィック共有ボタンを追加</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer select-none">
            <input
              type="checkbox"
              checked={shareCardEnabled}
              onChange={(e) => setShareCardEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-neutral-300 dark:bg-neutral-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-neutral-850 dark:peer-checked:bg-neutral-100" />
          </label>
        </div>

        {/* 7. 読了予測時間の表示 */}
        <div className="flex items-center justify-between p-3 rounded-md border border-neutral-500/10">
          <div className="space-y-0.5">
            <div className="text-xs font-bold">この章の残り時間を表示</div>
            <div className="text-[10px] opacity-40">読書速度（CPM）から章内の残り時間を算出</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showRemainingTime}
              onChange={(e) => setShowRemainingTime(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-neutral-300 dark:bg-neutral-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-neutral-850 dark:peer-checked:bg-neutral-100" />
          </label>
        </div>
      </div>

      <hr className="border-current/10 my-1" />

      {/* === 高度な演出設定（アコーディオン） === */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setShowEffectsAccordion(!showEffectsAccordion)}
          className={`w-full py-3 px-4 rounded-md border flex items-center justify-between text-xs font-bold transition-all ${
            isDark ? 'bg-neutral-850 border-neutral-800' : 'bg-neutral-100 border-neutral-200'
          } ${showEffectsAccordion ? 'ring-1 ring-amber-500/30' : ''}`}
        >
          <span>高度な演出設定（音響・発光など）</span>
          <motion.span
            animate={{ rotate: showEffectsAccordion ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="text-[10px]"
          >
            ▼
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
          {showEffectsAccordion && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="space-y-4 pt-1 overflow-hidden"
            >
              {/* 1. 触覚フィードバック (Haptics) */}
              <div className="flex items-center justify-between p-3 rounded-md border border-neutral-500/10">
                <div className="space-y-0.5">
                  <div className="text-xs font-bold">触覚フィードバック</div>
                  <div className="text-[10px] opacity-40">操作時に心地よい微振動を生成</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hapticsEnabled}
                    onChange={(e) => setHapticsEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-neutral-300 dark:bg-neutral-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-neutral-850 dark:peer-checked:bg-neutral-100" />
                </label>
              </div>

              {/* 2. オーディオ設定（効果音・BGM） */}
              <AudioSettingsSection
                soundVolume={soundVolume}
                setSoundVolume={setSoundVolume}
                bgmEnabled={bgmEnabled}
                setBgmEnabled={setBgmEnabled}
                bgmVolume={bgmVolume}
                setBgmVolume={setBgmVolume}
                bgmSound={bgmSound}
                setBgmSound={setBgmSound}
                activeBtnBg={activeBtnBg}
                inactiveBtnBg={inactiveBtnBg}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};
